import React, { CSSProperties, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { BotTemplateData, LanguageNameSpaces } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import IconWithFallback from "Global/IconWithFallback";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import usePublicBotCard from "features/bots/PublicBots/usePublicBotCard.ts";
import NewLookAreaChart from "features/bots/NewLook/NewLookAreaChart";
import {
  canDrawSparkline,
  onCardKeyDown,
} from "features/bots/NewLook/newLookBotUtils";
import {
  newLookCardInteractive,
  newLookCardSurface,
  newLookWell,
} from "features/bots/NewLook/newLookStyles";
import {
  getPublicBotChips,
  getPublicBotTagsText,
  toPercentText,
} from "features/bots/PublicBots/publicBotDisplay";
import {
  BotName,
  BotSub,
  BotSubDot,
  BotSubPart,
  QuietButton,
} from "features/bots/PublicBots/newLookPublicBotStyles";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import { PUBLIC_BOT_CARD_ID } from "testIds.ts";
import { phoneTwoLineTitle } from "common/NewLook/layout.ts";

interface NewLookPublicBotCardProps {
  botData: BotTemplateData;
  id?: string;
  botRank?: number;
  onBotClick?: () => void;
  prepareAccountOnClick?: boolean;
  cardStyle?: CSSProperties;
}

/**
 * New-look public bot card, a panel on the canvas with the "My bots" card's
 * anatomy:
 * 1. who — coin, name, what it trades · where, and a quiet copy action (the
 *    card itself opens the bot as well);
 * 2. how it's doing — the total profit as the one big figure, over its
 *    accumulated-profit chart (both P&L, so coloured by direction);
 * 3. the key figures (last 90 days, average month, days running) in a well;
 * 4. direction and margin as neutral chips, the bot's tags as a quiet line.
 */
const NewLookPublicBotCard = ({
  botData,
  id,
  botRank,
  onBotClick,
  prepareAccountOnClick,
  cardStyle,
}: NewLookPublicBotCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const tokens = getNewLookTokens(useTheme());
  const {
    formattedChartData,
    onBotCardClick,
    onCopyClick,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    showNoExchangeModal,
  } = usePublicBotCard({
    botData,
    botRank,
    onBotClick,
    prepareAccountOnClick,
  });

  const symbol = botData.symbols?.[0];
  const sub = [symbol, botData.exchange].filter(Boolean);
  const chips = getPublicBotChips(botData);
  const tags = getPublicBotTagsText(botData);
  const sparkline = useMemo(
    () => formattedChartData.map((point) => point.y),
    [formattedChartData]
  );
  const figures = [
    {
      key: "last_90_days",
      label: t("public_bots.last_90_days"),
      value: toPercentText(botData.last_90_days_change),
      color: directionColor(tokens, botData.last_90_days_change),
    },
    {
      key: "avg_monthly",
      label: t("public_bots.avg_monthly"),
      value: toPercentText(botData.average_monthly_profit),
      color: directionColor(tokens, botData.average_monthly_profit),
    },
    {
      key: "days_running",
      label: t("public_bots.days_running"),
      value: botData.days_running,
    },
  ];
  // In a fixed-height slot (the onboarding carousels) the chart band gives
  // or takes the difference, and the zones sit a little closer.
  const fillsHeight = cardStyle?.height !== undefined;

  return (
    <>
      <StyledCard
        onClick={onBotCardClick}
        onKeyDown={onCardKeyDown(onBotCardClick)}
        tabIndex={0}
        id={id}
        style={cardStyle}
        $compact={fillsHeight}
        data-testid={PUBLIC_BOT_CARD_ID}
      >
        <Head>
          <IconCell>
            <IconWithFallback
              iconType={ICON_TYPES.COIN}
              coinOrExchangeName={symbol}
              iconStyle={ICON_STYLE}
            />
          </IconCell>
          <CardBotName
            style={NAME_AREA}
            title={botData.name}
            $wraps={!fillsHeight}
          >
            {botData.name}
          </CardBotName>
          <Action>
            <QuietButton type="button" onClick={onCopyClick}>
              {t("public_bots.copy_bot")}
            </QuietButton>
          </Action>
          <BotSub style={SUB_AREA}>
            {sub.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <BotSubDot />}
                <BotSubPart title={part}>{part}</BotSubPart>
              </React.Fragment>
            ))}
          </BotSub>
        </Head>
        <Result $fill={fillsHeight}>
          <ProfitRow>
            <HeroValue
              style={{ color: directionColor(tokens, botData.change) }}
            >
              {toPercentText(botData.change)}
            </HeroValue>
            <HeroCaption>{t("public_bots.total_profit")}</HeroCaption>
          </ProfitRow>
          {canDrawSparkline(sparkline) && (
            <ChartBand $fill={fillsHeight}>
              <NewLookAreaChart values={sparkline} height={CHART_HEIGHT} />
            </ChartBand>
          )}
        </Result>
        <Figures>
          {figures.map((figure) => (
            <Figure key={figure.key}>
              <FigureLabel>{figure.label}</FigureLabel>
              <FigureValue style={{ color: figure.color }}>
                {figure.value}
              </FigureValue>
            </Figure>
          ))}
        </Figures>
        <Settings>
          {chips.length > 0 && (
            <Meta title={chips.join(" · ")}>{chips.join(" · ")}</Meta>
          )}
          {tags && <Tags title={tags}>{tags}</Tags>}
        </Settings>
      </StyledCard>
      {showNoExchangeModal && (
        <NoExchangeConnectedModal
          placement="public_bot"
          onClose={() => setNoExchangeModalVisible(false)}
          action={t("public_bots.copy_a_bot")}
          open={noExchangeModalVisible}
        />
      )}
    </>
  );
};

export default React.memo(NewLookPublicBotCard);

const ICON_STYLE = { width: 40, height: 40, marginRight: 0 };
const NAME_AREA = { gridArea: "name" };
const SUB_AREA = { gridArea: "sub" };
/** The chart band: the card's centerpiece, the full inner width. */
const CHART_HEIGHT = 64;

const StyledCard = styled.div<{ $compact?: boolean }>`
  ${newLookCardSurface};
  ${newLookCardInteractive};
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$compact ? 12 : 16)}px;
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 20px;
  @media (max-width: 600px) {
    gap: 12px;
    height: auto;
  }
`;
const Head = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "icon name action"
    "icon sub action";
  align-items: center;
  column-gap: 12px;
  row-gap: 2px;
  min-height: 40px;
  min-width: 0;
`;
const IconCell = styled.div`
  grid-area: icon;
  display: flex;
`;
// On the narrowest phones the card itself is the action, so the name and
// market keep the whole line.
const Action = styled.div`
  grid-area: action;
  display: flex;
  align-items: center;
  @container (max-width: 300px) {
    display: none;
  }
`;
/** The total profit and, under it, its chart across the card. */
const Result = styled.div<{ $fill?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  ${(p) =>
    p.$fill &&
    css`
      flex: 1 1 auto;
      min-height: 0;
    `}
  @media (max-width: 600px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
`;
const ProfitRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  @media (max-width: 600px) {
    flex: none;
    flex-direction: column;
    gap: 0;
  }
`;
const HeroValue = styled.span`
  flex: none;
  ${newLookType.figureLarge};
  white-space: nowrap;
`;
const HeroCaption = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
/**
 * Full inner width. In a fixed-height slot it takes whatever height is
 * left (at least 32px), and the SVG stretches to it.
 */
const ChartBand = styled.div<{ $fill?: boolean }>`
  min-width: 0;
  ${(p) =>
    p.$fill &&
    css`
      flex: 1 1 auto;
      min-height: 32px;
      display: flex;
      > div,
      svg {
        height: 100% !important;
      }
    `}
  @media (max-width: 600px) {
    flex: 0 1 160px;
    > div,
    svg {
      height: 36px !important;
    }
  }
`;
/**
 * The key figures in a well: equal columns split by hairlines, a label over
 * its figure. Labels wrap (never cut) and the figures share a baseline.
 */
const Figures = styled.div`
  ${newLookWell};
  flex: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  min-width: 0;
  padding: 12px 0;
`;
const Figure = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  min-width: 0;
  padding: 0 12px;
  &:not(:first-child) {
    box-shadow: inset 1px 0 0 ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const FigureLabel = styled.span`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  overflow-wrap: break-word;
`;
const FigureValue = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
/**
 * One line: chips that don't fit wrap onto a hidden second line (whole
 * chips, never cut); the tags sit on the right and give way first.
 */
const Settings = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 6px;
  height: 22px;
  margin-top: auto;
  overflow: hidden;
  min-width: 0;
`;
/** Direction · margin · amount as one quiet line of meta text. */
const Meta = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Tags = styled.span`
  flex: none;
  margin-left: auto;
  padding-left: 6px;
  ${newLookType.meta};
  line-height: 22px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const CardBotName = styled(BotName)<{ $wraps: boolean }>`
  ${(p) => p.$wraps && phoneTwoLineTitle};
`;
